import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LanguageCode, SUPPORTED_LANGUAGES, languageFromPath } from '../i18n/languages';
import { isTranslationLoaded, loadPublicTranslation, pubPath, articleForPath, automaticPublicLanguagePath } from '../i18n/publicTranslation';
import LoadingBook from '../components/LoadingBook';
export { SUPPORTED_LANGUAGES } from '../i18n/languages';
export type { LanguageCode } from '../i18n/languages';
interface LanguageContextType {
    currentLanguage: LanguageCode;
    setLanguage: (lang: LanguageCode) => void;
    getLocalizedPath: (path: string) => string;
    isLanguageRoute: boolean;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export const useLanguage = () => {
    const value = useContext(LanguageContext);
    if (!value)
        throw new Error('useLanguage must be used within a LanguageProvider');
    return value;
};
export const LanguageProvider: React.FC<{
    children: ReactNode;
}> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [automaticPath, setAutomaticPath] = useState(() => automaticPublicLanguagePath(
        location.pathname + location.search + location.hash,
        typeof navigator === 'undefined' ? [] : [...(navigator.languages || []), navigator.language || ''],
    ));
    const currentLanguage = languageFromPath(location.pathname);
    const article = articleForPath(location.pathname);
    const translationKey = currentLanguage + ':' + (article || 'common');
    const [loadedKey, setLoadedKey] = useState<string | null>(isTranslationLoaded(currentLanguage, article) ? translationKey : null);
    const [error, setError] = useState(false);
    useEffect(() => {
        if (automaticPath) {
            navigate(automaticPath, { replace: true });
            setAutomaticPath(null);
        }
    }, [automaticPath, navigate]);
    useEffect(() => {
        let cancelled = false;
        setError(false);
        loadPublicTranslation(currentLanguage, article).then(() => { if (!cancelled)
            setLoadedKey(translationKey); }).catch(() => { if (!cancelled)
            setError(true); });
        document.documentElement.lang = currentLanguage;
        document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';
        return () => { cancelled = true; document.documentElement.lang = 'en'; document.documentElement.dir = 'ltr'; };
    }, [currentLanguage, article, translationKey]);
    const value = {
        currentLanguage,
        isLanguageRoute: currentLanguage !== 'en',
        setLanguage: (lang: LanguageCode) => navigate(pubPath(location.pathname, lang) + location.search + location.hash),
        getLocalizedPath: (path: string) => pubPath(path, currentLanguage),
    };
    return <LanguageContext.Provider value={value}>
  {!automaticPath && (loadedKey === translationKey || isTranslationLoaded(currentLanguage, article)) ? <React.Fragment key={currentLanguage}>{children}</React.Fragment> : error && !automaticPath ?
            <main data-route-loading="language" style={{ padding: 48, textAlign: 'center' }}><p>{SUPPORTED_LANGUAGES[currentLanguage].name}</p><button onClick={() => window.location.reload()}>↻</button><Link to={pubPath(location.pathname, 'en') + location.search + location.hash}>English</Link></main> : <div data-route-loading="language" role="status" aria-label={SUPPORTED_LANGUAGES[currentLanguage].name} style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><LoadingBook /></div>}
 </LanguageContext.Provider>;
};
